import { useEffect, useState } from "react";
import { api, formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import PageHeader from "@/components/PageHeader";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { CreditCard, Loader2, CheckCircle2, Sparkles } from "lucide-react";
import { toast } from "sonner";

const eur = (c) => new Intl.NumberFormat("fr-FR", { style: "currency", currency: "EUR" }).format((c || 0) / 100);
const STATUS = {
  trialing: { label: "Essai gratuit", cls: "text-emerald-600 border-emerald-500/30" },
  active: { label: "Abonnement actif", cls: "text-emerald-600 border-emerald-500/30" },
  past_due: { label: "Paiement échoué", cls: "text-amber-600 border-amber-500/30" },
  canceled: { label: "Annulé", cls: "text-red-600 border-red-500/30" },
  expired: { label: "Expiré", cls: "text-red-600 border-red-500/30" },
  none: { label: "Aucun abonnement", cls: "text-slate-500 border-slate-300" },
  founder: { label: "Compte fondateur", cls: "text-emerald-600 border-emerald-500/30" },
};
const fmtDate = (iso) => (iso ? new Date(iso).toLocaleDateString("fr-FR", { day: "2-digit", month: "long", year: "numeric" }) : "—");

export default function Subscription() {
  const { user } = useAuth();
  const isOwner = user?.role === "owner";
  const [sub, setSub] = useState(null);
  const [cfg, setCfg] = useState(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    api.get("/subscription/me").then((r) => setSub(r.data)).catch(() => {});
    api.get("/platform/public-config").then((r) => setCfg(r.data)).catch(() => {});
  }, []);

  const subscribe = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/billing/checkout", { origin_url: window.location.origin });
      window.location.href = data.checkout_url;
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); setBusy(false); }
  };
  const portal = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/billing/portal", { origin_url: window.location.origin });
      window.location.href = data.url;
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); setBusy(false); }
  };

  const eff = sub?.effective_status || user?.subscription_status || "none";
  const st = STATUS[eff] || STATUS.none;
  const isActive = eff === "active";
  const isTrial = eff === "trialing";
  const isFounder = eff === "founder" || sub?.billing_exempt || user?.is_founder;

  return (
    <div>
      <PageHeader title="Mon abonnement" subtitle="Gérez votre formule Auto Detail Manager" icon={CreditCard} />
      {isFounder ? (
        <div className="max-w-2xl bg-gradient-to-br from-emerald-500 to-emerald-600 rounded-xl p-8 text-black" data-testid="founder-card">
          <div className="flex items-center gap-2 mb-2"><Sparkles className="w-6 h-6" /><span className="font-heading text-2xl font-extrabold">Compte fondateur — Gratuit à vie</span></div>
          <p className="text-sm font-medium mb-5">Aucun abonnement requis. Toutes les fonctionnalités sont incluses, aujourd'hui et à l'avenir.</p>
          <ul className="space-y-2 text-sm">
            {["Toutes les fonctionnalités incluses", "Aucun abonnement requis", "Aucune période d'essai", "Accès permanent et illimité"].map((b) => (
              <li key={b} className="flex items-center gap-2"><CheckCircle2 className="w-5 h-5" />{b}</li>
            ))}
          </ul>
        </div>
      ) : (
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-5 max-w-4xl">
        <div className="bg-white border border-slate-200 rounded-xl p-6" data-testid="subscription-card">
          <div className="flex items-center justify-between mb-4">
            <h3 className="font-heading text-base font-semibold text-slate-900">Statut</h3>
            <Badge variant="outline" className={`text-xs ${st.cls}`} data-testid="sub-status">{st.label}</Badge>
          </div>
          <dl className="space-y-2 text-sm">
            <Row k="Formule">{cfg?.plan_name || "Auto Detail Manager"}</Row>
            <Row k="Prix">{eur(sub?.price_monthly_cents ?? cfg?.price_monthly_cents ?? 1500)} / mois</Row>
            {isTrial && <Row k="Essai jusqu'au">{fmtDate(sub?.trial_end)}</Row>}
            {isActive && <Row k="Prochain renouvellement">{fmtDate(sub?.current_period_end)}</Row>}
          </dl>
        </div>

        <div className="bg-gradient-to-br from-emerald-500 to-emerald-600 rounded-xl p-6 text-black" data-testid="plan-card">
          <div className="flex items-center gap-2 mb-2"><Sparkles className="w-5 h-5" /><span className="font-heading text-lg font-bold">Formule unique</span></div>
          <div className="text-4xl font-extrabold">{eur(cfg?.price_monthly_cents ?? 1500)}<span className="text-base font-medium">/mois</span></div>
          <div className="text-sm font-medium mt-1 mb-4">14 jours d'essai gratuit · sans engagement</div>
          <ul className="space-y-1.5 text-sm mb-5">
            {["Toutes les fonctionnalités", "Clients, RDV, devis & factures", "Comptabilité & URSSAF", "Support inclus"].map((b) => (
              <li key={b} className="flex items-center gap-2"><CheckCircle2 className="w-4 h-4" />{b}</li>
            ))}
          </ul>
          {!isOwner ? (
            <p className="text-sm">Seul le propriétaire peut gérer l'abonnement.</p>
          ) : isActive ? (
            <Button onClick={portal} disabled={busy} data-testid="btn-manage-billing" className="w-full bg-black text-white hover:bg-slate-900">{busy && <Loader2 className="w-4 h-4 animate-spin mr-2" />}Gérer le paiement</Button>
          ) : (
            <Button onClick={subscribe} disabled={busy} data-testid="btn-subscribe" className="w-full bg-black text-white hover:bg-slate-900">{busy && <Loader2 className="w-4 h-4 animate-spin mr-2" />}{isTrial ? "S'abonner maintenant" : "Réactiver mon abonnement"}</Button>
          )}
        </div>
      </div>
      )}
    </div>
  );
}

function Row({ k, children }) {
  return <div className="flex justify-between border-b border-slate-100 pb-2"><dt className="text-slate-500">{k}</dt><dd className="text-slate-900 font-medium">{children}</dd></div>;
}
